// FILE: src/components/SessionTimer.js
// Self-contained countdown timer for an exam session.
//
// Accepts durationMinutes from session (which comes from exams.js config).
// Uses useEffect + setInterval for the tick. Clears itself on unmount.
//
// Caller should pass a unique `key` prop (e.g. session.startedAt) so React
// fully unmounts + remounts this component — and resets the countdown —
// whenever a new session begins.
//
// onExpire is stored in a ref so the interval always calls the *current*
// version of the callback, avoiding stale-closure bugs. It is fired from a
// dedicated watchEffect rather than inside the setState updater (which would
// be a side-effect inside a pure function — an anti-pattern in React strict mode).

import React, { useState, useEffect, useRef } from 'react';

/** Format total seconds into H:MM:SS (hours shown only when needed) */
function formatTime(totalSeconds) {
  if (totalSeconds <= 0) return '0:00';

  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  const mm = String(m).padStart(h > 0 ? 2 : 1, '0');
  const ss = String(s).padStart(2, '0');

  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Returns the visual state of the timer based on seconds remaining */
function timerState(secondsLeft, totalSeconds) {
  if (secondsLeft <= 0) return 'expired';
  // Warning when less than 10% of time remains, or under 5 minutes — whichever is smaller
  if (secondsLeft <= Math.min(totalSeconds * 0.1, 300)) return 'warning';
  return 'normal';
}

const stateStyles = {
  normal:  { color: '#333',    fontWeight: '600' },
  warning: { color: '#c97a00', fontWeight: '700' },
  expired: { color: '#c62828', fontWeight: '700' },
};

const wrapperStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
  fontSize: '1.35rem',
  fontVariantNumeric: 'tabular-nums',
};

/**
 * @param {number}   durationMinutes  - from session.durationMinutes (set by exams.js)
 * @param {function} [onExpire]       - called exactly once when the timer reaches zero
 * @param {boolean}  [paused]         - when true, the countdown holds its current
 *                                      value (used by PMI-style exam breaks — break
 *                                      time must never reduce exam time)
 * @param {function} [onTick]          - called with the current secondsLeft on mount
 *                                      and after every tick (including the final 0).
 *                                      Optional and side-effect-free for the timer
 *                                      itself: existing callers that omit it are
 *                                      unaffected. Pace coaching uses it to derive
 *                                      elapsed = totalSeconds - secondsLeft, which
 *                                      stays correct across paused break time.
 */
export default function SessionTimer({ durationMinutes, onExpire, paused = false, onTick }) {
  const totalSeconds = durationMinutes * 60;
  const [secondsLeft, setSecondsLeft] = useState(totalSeconds);

  // Ref mirror of `paused` so the single mounted interval (empty-deps effect)
  // always reads the current value without re-subscribing.
  const pausedRef = useRef(paused);
  useEffect(() => {
    pausedRef.current = paused;
  });

  // Keep a ref to onExpire so the interval closure always has the latest version
  // even though the interval effect runs only once (empty deps / key-remount pattern).
  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  });

  // Same stale-closure-proof pattern for onTick.
  const onTickRef = useRef(onTick);
  useEffect(() => {
    onTickRef.current = onTick;
  });

  // ── Countdown tick ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (totalSeconds <= 0) return;

    const interval = setInterval(() => {
      setSecondsLeft((prev) => {
        if (pausedRef.current) return prev; // exam break — clock is stopped
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs once on mount; key-based remounting resets between sessions.

  // ── Expiry notification ─────────────────────────────────────────────────────
  // Separate from the tick so onExpire is never called inside a setState updater.
  useEffect(() => {
    if (secondsLeft === 0 && totalSeconds > 0) {
      onExpireRef.current?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  // ── Tick notification ───────────────────────────────────────────────────────
  // Publishes secondsLeft upward (mount + every change) without coupling the
  // timer to any consumer. Fired from a watch effect, never inside the updater.
  useEffect(() => {
    onTickRef.current?.(secondsLeft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  // ── Render ──────────────────────────────────────────────────────────────────
  const state = timerState(secondsLeft, totalSeconds);
  const isExpired = state === 'expired';

  return (
    <div data-testid="session-timer" style={wrapperStyle}>
      <span role="img" aria-label="timer" style={{ fontSize: '1.28rem' }}>
        ⏱
      </span>
      <span style={stateStyles[state]}>
        {isExpired ? "Time's up" : formatTime(secondsLeft)}
      </span>
    </div>
  );
}
